import React from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { useChartTokens } from './chartTokens';

/**
 * Shared widget primitives.
 *
 * The rule these exist to enforce: **null is not zero.** The API returns null
 * for "not enough data to compute" — OEE with no runtime, win rate with no
 * closed deals, changePct with no baseline. Rendering that as 0 tells a
 * finance user their win rate is 0%, which is a different and alarming claim.
 * Every helper here renders an em dash for null and a real number only when
 * there is one.
 */

/** Indian numbering (lakh/crore), which is what the whole app formats to. */
export const formatINR = (value: number | null | undefined, compact = false): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const n = Number(value);
  if (!compact) return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Number(value).toLocaleString('en-IN');
};

export const formatPct = (value: number | null | undefined, digits = 1): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${Number(value).toFixed(digits)}%`;
};

export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

export const formatRelative = (value: string | Date | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value).getTime();
  if (Number.isNaN(d)) return '—';
  const mins = Math.round((Date.now() - d) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
};

/**
 * The hero number of a stat widget.
 *
 * Not a chart — when the data's job is "one figure", a chart is decoration.
 */
export const StatValue: React.FC<{
  value: React.ReactNode;
  label?: string;
  caption?: string;
  size?: 'sm' | 'md' | 'lg';
}> = ({ value, label, caption, size = 'md' }) => {
  const fontSize = size === 'lg' ? '2.25rem' : size === 'sm' ? '1.25rem' : '1.75rem';
  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
          {label}
        </Typography>
      )}
      <Typography
        sx={{
          fontSize,
          fontWeight: 650,
          lineHeight: 1.15,
          letterSpacing: '-0.02em',
          // Stops the number jittering as digits change on refresh.
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Period-over-period change.
 *
 * `null` renders as "no baseline" rather than 0% — a first-month figure has no
 * comparison, and "0% change" would be a fabrication.
 */
export const DeltaChip: React.FC<{
  value: number | null | undefined;
  /** For costs, down is good. */
  invert?: boolean;
  suffix?: string;
}> = ({ value, invert = false, suffix = '' }) => {
  const t = useChartTokens();

  if (value === null || value === undefined || Number.isNaN(value)) {
    return (
      <Tooltip title="Not enough history to compare">
        <Typography variant="caption" color="text.disabled">
          no baseline
        </Typography>
      </Tooltip>
    );
  }

  const n = Number(value);
  const flat = Math.abs(n) < 0.05;
  const good = invert ? n < 0 : n > 0;
  const color = flat ? t.textSecondary : good ? t.status.good : t.status.critical;
  const Icon = flat ? TrendingFlat : n > 0 ? TrendingUp : TrendingDown;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {/* Icon as well as colour — direction is never colour-alone. */}
      <Icon sx={{ fontSize: 15, color }} />
      <Typography variant="caption" sx={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {n > 0 ? '+' : ''}
        {n.toFixed(1)}%{suffix}
      </Typography>
    </Box>
  );
};

/**
 * Ranked horizontal bars with the value direct-labelled.
 *
 * Used instead of a pie/donut for "top N" — length on a common baseline is far
 * easier to compare than angle, and the label satisfies the light-mode
 * contrast obligation on the categorical palette.
 */
export const MiniBarList: React.FC<{
  items: Array<{ label: string; value: number; color?: string }>;
  formatValue?: (v: number) => string;
  max?: number;
}> = ({ items, formatValue = formatNumber, max }) => {
  const t = useChartTokens();
  const ceiling = max ?? Math.max(...items.map((i) => i.value), 1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {items.map((item, i) => (
        <Box key={item.label}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
              {item.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatValue(item.value)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min((item.value / ceiling) * 100, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: t.grid,
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: item.color || t.series(i),
              },
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

/** Compact key/value rows for list-style widgets. */
export const StatRow: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  color?: string;
}> = ({ label, value, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      py: 0.6,
    }}
  >
    <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, flexShrink: 0, color, fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
  </Box>
);

/** Status pill. Always carries a label, never colour alone. */
export const StatusPill: React.FC<{ label: string; role: 'good' | 'warning' | 'serious' | 'critical' }> = ({
  label,
  role,
}) => {
  const t = useChartTokens();
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 0.875,
        py: 0.25,
        borderRadius: 999,
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1.6,
        color: t.status[role],
        backgroundColor: `${t.status[role]}1f`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
};
